import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Label, MenuItem, Row, Sidebar } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { NAVBAR_HEIGHT } from '~/constants/layout';
import { ConfirmPopup } from '~/commonComponents';
import '~/commonComponents/SelectProfilePopup/OfficeSwitcher.scss';
import { getOfficeSwitcherHeaderProps } from '~/commonComponents/SelectProfilePopup/officeSwitcherHeaderProps';
import { OfficeSwitcherPanel } from '~/commonComponents/SelectProfilePopup/OfficeSwitcherPanel';
import { SelectProfessionTypePopup } from '~/components/ShiftRequestRedesign/SelectProfessionType/SelectProfessionTypePopup';
import { useSelectProfessionType } from '~/components/ShiftRequestRedesign/SelectProfessionType/useSelectProfessionType';
import { useUserSelector } from '~/hooks/useUserSelector';
import ArrowIcon from '~/images/arrow-switch-horizontal.png';
import {
  OutlineBriefcaseIcon,
  OutlineCalendarCheckIcon,
  OutlineDashboardIcon,
  OutlineOfficeBuildingIcon,
  OutlineUserGroupIcon,
  OutlineWarning01Icon,
} from '@tempmee/react-icons';
import { routes } from '~/routes';
import { useIsWorkerClassificationEnabled, useRequestControls } from '~/selectors';
import {
  fireMenuBarDashboardClicked,
  fireMenuBarLocationSettingsClicked,
  fireMenuBarProfessionalHubClicked,
  fireMenuBarScheduleClicked,
  fireMenuBarShiftRequestClicked,
} from '~/posthogs/events';
import { searchChildOffice, selectOffice } from '../actions';
import browserHistory from '../browserHistory';
import { getShouldUseSidebarRequestProfessionalMenu } from '../growthbook';
import { AccountSplitWeb } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';

const navIconColor = (isActive: boolean) => (isActive ? colors.shades[0] : colors.neutral[600]);

/**
 * Keeps the entire sidebar pinned below the navbar as the page scrolls.
 * alignSelf:flex-start prevents flex-stretch from making the sidebar the full
 * Row height (which would defeat sticky — an element already filling its
 * container has nowhere to stick). With flex-start, the sidebar is only as
 * tall as its content (+ minHeight) and sticks at top:NAVBAR_HEIGHT.
 */
const sidebarStickyStyle = {
  position: 'sticky' as const,
  top: NAVBAR_HEIGHT,
  alignSelf: 'flex-start' as const,
};

export default function SideBar() {
  const dispatch = useDispatch();
  const { location } = browserHistory;
  const childOffices = useSelector((state: any) => state.user.childOffices);
  const selectedChildOffice = useSelector((state: any) => state.user.selectedChildOffice);
  const masterOfficeId = useSelector((state: any) => state.user?.user?.id);
  const allPPJobs = useSelector((state: any) => state.pp.allPPJobs);
  const user = useUserSelector();

  const allowedRequest = user?.allowedRequest || {};
  const [shiftRequestsUnavailableModalOpen, setShiftRequestsUnavailableModalOpen] = useState(false);
  const [showOfficeSwitcher, setShowOfficeSwitcher] = useState(false);
  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();
  const showWorkerClassificationWarning =
    isWorkerClassificationEnabled && !user?.workerClassification?.classificationType;

  const [showProfessionPopup, setShowProfessionPopup] = useState(false);
  const officeState = user?.address?.[0]?.state ?? '';
  const { availableProfessions } = useSelectProfessionType({ officeState });

  const allowedRequestProfessionalMenuStates = getShouldUseSidebarRequestProfessionalMenu();
  const isAllowedRequestProfessionalMenuEnabled =
    allowedRequestProfessionalMenuStates.length > 0 &&
    allowedRequestProfessionalMenuStates.includes(user.address?.[0]?.state);

  const { isPermProductAllowed } = useRequestControls();
  const isAccountSplitOn = useFeatureFlag(AccountSplitWeb);
  const isLocationSettingsActive = location.pathname.startsWith(routes.locations);
  const hasPostings = useSelector((state: any) => state.permanentJobs.hasPostings);
  const hasPublishedPostings = useSelector(
    (state: any) => state.permanentJobs.hasPublishedPostings,
  );

  const isPostShiftsActive =
    location.pathname.startsWith('/dashboard/shift-request/') ||
    location.pathname.startsWith('/dashboard/multi-day/');

  const handlePostShiftsClick = () => {
    if (showWorkerClassificationWarning) {
      setShiftRequestsUnavailableModalOpen(true);
      return;
    }
    if (isPostShiftsActive) return;
    fireMenuBarShiftRequestClicked();
    setShowProfessionPopup(true);
  };

  const renderPPLink = () => {
    if (!allPPJobs || allPPJobs.length === 0) return null;

    const isPPActive = location.pathname.includes('/dashboard/permanentJobs');
    const ppRoute =
      allPPJobs.length === 1
        ? `/dashboard/permanentJobs/${allPPJobs[0].id}`
        : '/dashboard/permanentJobs';
    const ppTitle = allPPJobs.length === 1 ? 'Permanent Job' : 'Permanent Jobs';

    return (
      <MenuItem
        icon={<OutlineBriefcaseIcon color={navIconColor(isPPActive)} />}
        selected={isPPActive}
        onClick={() => browserHistory.push(ppRoute)}
      >
        {ppTitle}
      </MenuItem>
    );
  };

  return (
    <>
      <Sidebar
        data-cy="sidebar"
        width={300}
        style={{
          // Fill the space below the navbar on short pages while staying sticky
          // through the full scroll on tall pages.
          minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          ...sidebarStickyStyle,
          // Sit above page content with a right-edge shadow so full-bleed page
          // headers (e.g. the Shift Summary top-card banner) appear to tuck
          // under the sidebar instead of butting flush against it.
          zIndex: 20,
          boxShadow: '6px 0 12px -4px rgba(0, 0, 0, 0.12)',
          // position:sticky creates its own stacking context, so the dropdown's
          // z-index only competes inside the sidebar. While the office switcher
          // is open, lift the whole sidebar above the page content so nothing
          // (e.g. the "< Back" button) paints over the dropdown (GOTU-3780).
          ...(showOfficeSwitcher ? { zIndex: 1000 } : {}),
        }}
      >
        {/* GOTU-8264: with the account split on, the location switcher lives in the top bar. */}
        {!isAccountSplitOn && childOffices && childOffices.length > 0 && (
          <>
            <Sidebar.Header
              data-cy="office-switcher-trigger"
              className="office-switcher-header"
              onClick={() => setShowOfficeSwitcher(true)}
              style={{ cursor: 'pointer' }}
              {...getOfficeSwitcherHeaderProps(selectedChildOffice, masterOfficeId)}
              action={
                <img src={ArrowIcon} height={24} width={24} alt="switch office" />
              }
            />
            {showOfficeSwitcher && (
              <OfficeSwitcherPanel
                onClose={() => setShowOfficeSwitcher(false)}
                onOfficeClicked={(officeId) => dispatch(selectOffice({ officeId }))}
                onSearch={(searchText) => dispatch(searchChildOffice({ searchText }))}
              />
            )}
          </>
        )}

        <Sidebar.Content>
          <MenuItem
            data-cy="sidebar-dashboard"
            icon={<OutlineDashboardIcon color={navIconColor(location.pathname === '/')} />}
            selected={location.pathname === '/'}
            onClick={() => {
              browserHistory.push('/');
              fireMenuBarDashboardClicked();
            }}
          >
            Dashboard
          </MenuItem>

          {renderPPLink()}

          <MenuItem
            icon={<OutlineCalendarCheckIcon color={navIconColor(location.pathname === '/dashboard/calendar')} />}
            selected={location.pathname === '/dashboard/calendar'}
            onClick={() => {
              browserHistory.push('/dashboard/calendar');
              fireMenuBarScheduleClicked();
            }}
          >
            Schedule
          </MenuItem>

          <MenuItem
            data-cy="sidebar-professionals-hub"
            icon={
              <OutlineUserGroupIcon color={navIconColor(location.pathname.includes('/professionals-hub'))} />
            }
            selected={location.pathname.includes('/professionals-hub')}
            onClick={() => {
              browserHistory.push('/professionals-hub/favorites-list');
              fireMenuBarProfessionalHubClicked();
            }}
          >
            Professionals Hub
          </MenuItem>

          {isPermProductAllowed && (
            <MenuItem
              icon={
                <OutlineBriefcaseIcon
                  color={navIconColor(location.pathname.startsWith('/permanent-jobs'))}
                />
              }
              selected={location.pathname.startsWith('/permanent-jobs')}
              onClick={() =>
                browserHistory.push(
                  hasPostings === false
                    ? '/permanent-jobs/value-proposition'
                    : '/permanent-jobs/postings',
                )
              }
            >
              {hasPublishedPostings === false ? (
                <Row align="center" style={{ gap: 4 }}>
                  Permanent Jobs <Label severity="info" variant="filled">NEW</Label>
                </Row>
              ) : (
                'Permanent Jobs'
              )}
            </MenuItem>
          )}

          {isAccountSplitOn && (
            <MenuItem
              data-cy="sidebar-location-settings"
              icon={<OutlineOfficeBuildingIcon color={navIconColor(isLocationSettingsActive)} />}
              selected={isLocationSettingsActive}
              onClick={() => {
                fireMenuBarLocationSettingsClicked();
                browserHistory.push(routes.locations);
              }}
            >
              Location settings
            </MenuItem>
          )}

          {/* Post Shifts is a CTA (mint `cta` gradient), not a nav item — per the
              sidebar design. It flows as the last item in the nav, not pinned to the bottom. */}
          {isAllowedRequestProfessionalMenuEnabled && allowedRequest?.tempOrMdbShifts && (
            <Button
              variant="contained"
              color="cta"
              onClick={handlePostShiftsClick}
              data-cy="sidebar-post-shift-button"
              style={{ width: '100%', marginTop: 16 }}
            >
              Post shift
            </Button>
          )}
        </Sidebar.Content>
      </Sidebar>

      {shiftRequestsUnavailableModalOpen && showWorkerClassificationWarning && (
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <OutlineWarning01Icon size={80} color={colors.warning[500]} />
            </div>
          )}
          title="Shift Requests Unavailable"
          description="Shift Requests are unavailable until a worker type has been chosen for this office. Please visit the Worker Type Settings page to select a preference."
          descriptionStyle={{ marginBottom: 40 }}
          leftButtonAction={() => setShiftRequestsUnavailableModalOpen(false)}
          leftButtonText="Close"
          leftButtonStyle={{ width: 170 }}
          rightButtonAction={() => {
            setShiftRequestsUnavailableModalOpen(false);
            browserHistory.push(routes.workerClassificationType);
          }}
          rightButtonText="Set Preference"
          rightButtonStyle={{ width: 170 }}
        />
      )}

      {showProfessionPopup && (
        <SelectProfessionTypePopup
          professions={availableProfessions}
          onContinue={(professionCode) => {
            setShowProfessionPopup(false);
            browserHistory.push(routes.selectDates, {
              professionType: professionCode,
            });
          }}
          onClose={() => setShowProfessionPopup(false)}
        />
      )}
    </>
  );
}
